import React, { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { openExternalLink } from '@/lib/navigation';
import { BookMarkedIcon, FileDownIcon, HighlighterIcon, MonitorIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { useAdminPerms } from '@/hooks/auth';
import { useLiveConsoleHistory } from '@/pages/LiveConsole/liveConsoleHooks';
import { useAtomValue } from 'jotai';
import { fxRunnerStateAtom } from '@/hooks/status';
import { findProtectedConvar } from '@shared/protectedConvars';


type ConsoleFooterButtonProps = {
    icon: React.ElementType;
    title: string;
    disabled?: boolean;
    onClick: () => void;
}

function ConsoleFooterButton({ icon: Icon, title, disabled, onClick }: ConsoleFooterButtonProps) {
    return (
        <div
            tabIndex={0}
            className={cn(
                'pressable group w-full min-h-11 lg:min-h-0 lg:h-9 rounded-tile px-2 cursor-pointer',
                'flex items-center justify-center outline-none',
                'border border-edge bg-tile xs:border-transparent xs:bg-transparent',
                'hover:border-edge hover:bg-tile',
                'font-rad text-label font-semibold uppercase tracking-[.12em] text-faint scrim',
                'hover:text-fg',
                disabled && 'opacity-40 pointer-events-none'
            )}
            onClick={() => !disabled && onClick()}
            onKeyDown={(e) => (e.code === 'Enter' || e.code === 'Space') && !disabled && onClick()}
        >
            <Icon className="size-4 shrink-0 drop-scrim" strokeWidth={2.25} />
            <span className="hidden 2xl:inline ml-1.5 align-middle">
                {title}
            </span>
        </div>
    )
}


type LiveConsoleFooterProps = {
    highlightTerm: string;
    setHighlightTerm: (_term: string) => void;
    isConnected: boolean;
    consoleWrite: (_data: string) => void;
    consoleReportBlocked: (_cmd: string) => void;
    consoleClear: () => void;
    toggleSaveSheet: () => void;
    toggleSearchBar: () => void;
    termInputRef: React.RefObject<HTMLInputElement>;
}

export default function LiveConsoleFooter(props: LiveConsoleFooterProps) {
    const { history, appendHistory } = useLiveConsoleHistory();
    const [histIndex, setHistIndex] = useState(-1);
    //a credential convar in the field is never submitted, only reported
    const [blockedConvar, setBlockedConvar] = useState<string | undefined>();
    const savedInput = useRef('');
    const termInputRef = props.termInputRef;
    const { hasPerm } = useAdminPerms();
    const hasWritePerm = hasPerm('console.write');
    const canUseSvGui = hasPerm('all_permissions');
    const fxRunnerState = useAtomValue(fxRunnerStateAtom);

    //autofocus on input when connected
    useEffect(() => {
        if (props.isConnected && termInputRef.current) {
            termInputRef.current.focus();
        }
    }, [props.isConnected, termInputRef]);

    //the history arrows write to the field directly, so the check has to be re-run
    const syncBlockedConvar = () => {
        setBlockedConvar(findProtectedConvar(termInputRef.current?.value ?? ''));
    };

    const handleArrowUp = () => {
        if (!termInputRef.current) return;
        if (histIndex === -1) {
            savedInput.current = termInputRef.current.value ?? '';
        }
        const nextHistId = histIndex + 1;
        if (history[nextHistId]) {
            termInputRef.current.value = history[nextHistId];
            setHistIndex(nextHistId);
            syncBlockedConvar();
        }
    };

    const handleArrowDown = () => {
        if (!termInputRef.current) return;
        const prevHistId = histIndex - 1;
        if (prevHistId === -1) {
            termInputRef.current.value = savedInput.current;
            setHistIndex(prevHistId);
        } else if (history[prevHistId]) {
            termInputRef.current.value = history[prevHistId];
            setHistIndex(prevHistId);
        }
        syncBlockedConvar();
    };

    const handleEnter = () => {
        if (!termInputRef.current) return;
        const currentInput = termInputRef.current.value.trim();

        //Never sent, the server only gets told that it was attempted
        if (findProtectedConvar(currentInput)) {
            props.consoleReportBlocked(currentInput);
            return;
        }

        setHistIndex(-1);
        termInputRef.current.value = '';
        savedInput.current = '';
        setBlockedConvar(undefined);
        if (currentInput) {
            appendHistory(currentInput);
            props.consoleWrite(currentInput);
        } else {
            props.consoleWrite('\n');
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!props.isConnected) return;
        if (e.key === 'ArrowUp') {
            handleArrowUp();
            e.preventDefault();
        } else if (e.key === 'ArrowDown') {
            handleArrowDown();
            e.preventDefault();
        } else if (e.key === 'Enter') {
            handleEnter();
            e.preventDefault();
        }
    }

    let inputError: string | undefined;
    if (!hasWritePerm) {
        inputError = 'You do not have permission to write to the console.';
    } else if (!fxRunnerState.isChildAlive) {
        inputError = 'The server is not running.';
    } else if (!props.isConnected) {
        inputError = 'Socket connection lost.';
    }

    return (
        <div className="flex flex-col xs:flex-row xs:items-center gap-2 px-1 sm:px-4 py-2 border-t border-edge justify-center">
            <div className="flex items-center grow">
                <svg
                    className="hidden sm:block w-4 h-4 mr-2 text-accent shrink-0 drop-scrim"
                    fill="none"
                    height="24"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="m9 18 6-6-6-6" />
                </svg>
                <Input
                    ref={termInputRef}
                    className={cn(
"w-full",
                        !!inputError && 'placeholder:text-danger-soft placeholder:opacity-100',
                        !!blockedConvar && '!border-danger !text-danger-soft'
                    )}
                    placeholder={inputError ?? "Type a command..."}
                    type="text"
                    disabled={!!inputError}
                    onKeyDown={handleInputKeyDown}
                    onChange={syncBlockedConvar}
                    autoCapitalize='none'
                    autoComplete='off'
                    autoCorrect='off'
                />
            </div>
            <div className="flex items-center shrink-0">
                <HighlighterIcon
                    className="hidden sm:block size-4 mr-2 text-amber shrink-0 drop-scrim"
                    strokeWidth={2.25}
                />
                <Input
                    className="w-full xs:w-40"
                    placeholder="Highlight..."
                    type="text"
                    value={props.highlightTerm}
                    onChange={(e) => props.setHighlightTerm(e.target.value)}
                    autoCapitalize="none"
                    autoComplete="off"
                    autoCorrect="off"
                />
            </div>
            <div className="flex flex-row justify-evenly gap-3 2xl:gap-1 select-none">
                <ConsoleFooterButton
                    icon={BookMarkedIcon}
                    title="Saved"
                    onClick={props.toggleSaveSheet}
                />
                <ConsoleFooterButton
                    icon={SearchIcon}
                    title="Search"
                    disabled={!props.isConnected}
                    onClick={props.toggleSearchBar}
                />
                <ConsoleFooterButton
                    icon={FileDownIcon}
                    title="Download"
                    disabled={!props.isConnected}
                    onClick={() => {
                        openExternalLink('/fxserver/downloadLog');
                    }} />
                <ConsoleFooterButton
                    icon={Trash2Icon}
                    title="Clear"
                    disabled={!props.isConnected}
                    onClick={props.consoleClear}
                />
                {canUseSvGui ? (
                    <ConsoleFooterButton
                        icon={MonitorIcon}
                        title="svGUI"
                        disabled={!!inputError}
                        onClick={() => props.consoleWrite('svgui')}
                    />
                ) : null}
            </div>
        </div>
    );
}
